import Button from '../shared/Button.js';
import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import InlineNotice from '../shared/InlineNotice.jsx';
import RecordPageLoadError from '../shared/RecordPageLoadError.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
import {processingStatusLabel} from '../lib/processingStatusDisplay.js';

import {navigateToProcessingRoute, processingSourceRoute} from '../lib/processingNav.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
/* eslint-disable no-unused-vars -- shell primitives are used in JSX only */
import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageNotFound,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
/* eslint-enable no-unused-vars */
import {
  recordFormCard,
  recordFieldRowClass,
  recordFieldLabel,
  recordControl,
  recordTextarea,
} from '../shared/recordPageControls.jsx';
import {detachSheepFromProcessingBatch} from '../lib/processingDetachApi.js';
import {invalidateSheepWeighInsCache} from '../lib/sheepCache.js';
import {newSheepRequestId} from '../lib/sheepBatchOperationApi.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {SheepBatchDetailData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
// processingBatchDeleteApi is no longer imported here. It is still the RPC's
// owner — the catalog's sheep.batch.delete entry calls it — but this page now
// reaches it through the entry, so the delete arrives with its invalidation set
// attached instead of with none.
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {SheepBatchDeleteVars, SheepBatchPatchVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import {
  computeRuminantBatchTotals,
  RuminantBatchStat,
  RuminantBatchStatsGrid,
  ruminantBatchDetachButtonStyle,
  ruminantBatchRowCardStyle,
  ruminantBatchWeightLabelStyle,
} from '../cattle/ruminantPresentation.js';
import {reportSaveFailed, reportSaved, reportSaving} from '../lib/saveStatus.js';
import Header from '../shared/Header.tsx';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import type {RecordSeqEntry, RecordSeqId} from '../lib/recordSequence.js';
import type {RpcEnvelope, SupabaseClient} from '../lib/supabaseTypes.js';
import type {ISODateInput} from '../lib/dateUtils.js';

// Both route-owned reads are `select('*')`, so each row is an open record with
// only the columns this page actually names declared — the same decision
// routeDataCatalog.ts records. Nothing here invents a schema TypeScript cannot
// verify against the SQL.
//
// The weights are `number | string | null` because sheep_detail is jsonb the
// client itself writes: saveSheepWeight stores a parsed number, while rows
// imported before that path existed can still carry the raw text.
type SheepBatchDetailRow = {
  [column: string]: unknown;
  sheep_id: string;
  tag?: string | null;
  live_weight?: number | string | null;
  hanging_weight?: number | string | null;
};
type SheepProcessingBatchRow = {
  [column: string]: unknown;
  id: string;
  name: string | null;
  status: string | null;
  planned_process_date: string | null;
  actual_process_date: string | null;
  processing_cost: number | null;
  notes: string | null;
  sheep_detail: SheepBatchDetailRow[] | null;
};
type SheepRow = {[column: string]: unknown; id: string; tag: string | null; breed: string | null};

// The metadata editor's draft. Every field is a string because every control is
// a text/date/number input or a select.
//
// The fields are OPTIONAL because the six updaters spread a state TypeScript
// knows can be null: `setMetaDraft((m) => ({...m, name: v}))` yields a one-key
// object when `m` is null. That branch is unreachable today — the editor only
// renders once loadAll has set a complete draft — and declaring the fields
// optional is what lets every updater stay written exactly as it was instead of
// a guard that would change what the null case does.
type MetaDraft = {
  name?: string;
  planned_process_date?: string;
  actual_process_date?: string;
  processing_cost?: string;
  notes?: string;
  status?: string;
};

// Route state carrying the originating list order; `location.state` is
// `unknown` at the navigation seam by design, so the shape is declared here.
type SheepBatchRouteState = {recordSeq?: RecordSeqEntry[] | null};
type ErrorLike = {message?: string};

// The catalog rethrows PostgREST's `error.message` unchanged, so reading
// `.message` off the rejection reproduces the notice text the `{error}`
// envelope produced — including the "undefined" a message-less error would
// have rendered before. This is a conversion, not a rewording.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as ErrorLike).message) : String(err);

// `authState` arrives from SheepBatchesRouter's useAuth() read, which the sheep
// and cattle routers alike narrow to `AuthIdentity | null`.
type SheepBatchPageProps = {
  sb: SupabaseClient;
  fmt: (iso: ISODateInput) => string;
  authState: AuthIdentity | null;
};

// A blocked detach, carried to the summary notice with the sheep it belongs to.
type BlockedDetach = RpcEnvelope & {sheepId: string};

export default function SheepBatchPage({sb, fmt, authState}: SheepBatchPageProps) {
  const navigate = useNavigate();
  // `location.state` is `unknown` by design (src/app/navigation/appLocation.ts):
  // the write side normalized it and no consumer may read it without proving its
  // shape. The route state this page reads is exactly the one
  // recordSeqNavOptions writes, so that proof is applied ONCE, here at the hook
  // call, instead of at the read — which leaves the read the same expression it
  // has always been. The location shape is taken from the facade hook itself, so
  // no facade internal is imported.
  const location = useLocation() as Omit<ReturnType<typeof useLocation>, 'state'> & {
    state?: SheepBatchRouteState | null;
  };
  const batchId = location.pathname.slice('/sheep/batches/'.length);
  // Originating list order handed through route state; absent on direct links.
  const recordSeq = location.state?.recordSeq || null;
  function navigateSeq(id: RecordSeqId) {
    navigate('/sheep/batches/' + id, recordSeqNavOptions(recordSeq));
  }

  const role = authState && authState.role;
  const canEdit = role === 'admin' || role === 'management';

  // A record id is part of the resource key, so A -> B navigation clears A's
  // data and cancels its request instead of allowing a late response to paint
  // into B. The generic comments/activity section stays outside this resource:
  // it is a shared sidecar with its own entity-scoped lifecycle.
  const batchResource = useRouteResource(
    RESOURCE.SHEEP_BATCH_DETAIL,
    React.useMemo(() => ({batchId}), [batchId]),
  );
  const batchData = batchResource.data as SheepBatchDetailData | null;
  const batch = batchData ? (batchData.batch as SheepProcessingBatchRow | null) : null;
  const sheep = batchData ? (batchData.sheep as SheepRow[]) : [];
  const loading = batchResource.loading;
  const loadAll = batchResource.refetch;
  const batchError = batchResource.error as ErrorLike | null;
  const loadError: Notice | null = batchError
    ? {
        kind: 'error',
        message:
          'Could not load sheep processing batch. Please refresh the page. (' +
          (batchError.message || String(batchError)) +
          ')',
      }
    : null;
  const [notice, setNotice] = React.useState<Notice | null>(null);
  const [draft, setDraft] = React.useState<Record<string, string>>({});
  const [metaDraft, setMetaDraft] = React.useState<MetaDraft | null>(null);

  // The record page's two declared writes. Both are handed the `sb` this
  // surface was given rather than the module singleton, so the page keeps
  // exactly the client it has always written through.
  //
  // `patchBatch` serves all six metadata editors AND the weight grid: they are
  // one `.update()` on one row, and splitting them into two entries would
  // duplicate an identical invalidation set for no reader's benefit.
  const patchBatch = useRouteMutation<SheepBatchPatchVars, null>(MUTATION.SHEEP_BATCH_PATCH, {client: sb});
  const deleteBatch = useRouteMutation<SheepBatchDeleteVars, RpcEnvelope>(MUTATION.SHEEP_BATCH_DELETE, {client: sb});

  // MIGRATION 241: no client Activity here any more. sheep_batch_operation writes
  // the sheep.processing field.updated / status.changed rows (same bodies the
  // page used to write) in the same transaction as the change it describes, so a
  // refused write can no longer leave an Activity row, nor a saved one lose it.

  React.useEffect(() => {
    setNotice(null);
    setDraft({});
    setMetaDraft(null);
  }, [batchId]);

  // Metadata is an editor draft, not a second owner of the record. Seed it
  // only from a resolved scoped payload; after a successful mutation's refetch
  // the server remains the authoritative source for every visible field.
  React.useEffect(() => {
    if (!batchData) return;
    if (!batch) {
      setMetaDraft(null);
      return;
    }
    setMetaDraft({
      name: batch.name || '',
      planned_process_date: batch.planned_process_date || '',
      actual_process_date: batch.actual_process_date || '',
      processing_cost: batch.processing_cost != null ? String(batch.processing_cost) : '',
      notes: batch.notes || '',
      status: batch.status || 'planned',
    });
  }, [batch, batchData]);

  React.useEffect(() => {
    if (!loading && location.hash) {
      const anchor = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 200);
    }
  }, [loading, location.hash]);

  function sheepDetail(): SheepBatchDetailRow[] {
    // Same expression, hoisted into a local so Array.isArray narrows a
    // reference rather than an expression. `batch && batch.sheep_detail` is
    // unchanged, and Array.isArray answers false for null either way.
    const detail = batch && batch.sheep_detail;
    return Array.isArray(detail) ? detail : [];
  }
  async function saveSheepWeight(sheepId: string, field: string, value: string) {
    if (!canEdit || !batch) return;
    const currentRows = sheepDetail();
    const currentRow = currentRows.find((r) => r.sheep_id === sheepId);
    if (!currentRow) return;
    const oldValue = currentRow[field];
    const trimmed = value === '' || value == null ? null : parseFloat(value);
    if (trimmed != null && Number.isNaN(trimmed)) return;
    const newValue = trimmed;
    const oldNorm = oldValue == null ? null : oldValue;
    const newNorm = newValue == null ? null : newValue;
    if (oldNorm === newNorm) return;
    setNotice(null);
    // MIGRATION 241: ONE sheep's ONE cell, against the value this page READ.
    // The server changes only that cell under the batch row lock and recomputes
    // both totals from the locked array, so an attach or detach that landed
    // since this page loaded is never overwritten, and two editors' different
    // cells both survive. A stale cell is refused (conflict), never written.
    const saveKey = `sheep-batch-weight:${batch.id}:${sheepId}:${field}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      await patchBatch.run({
        requestId: newSheepRequestId(),
        operation: 'batch.weight',
        batchId: batch.id,
        sheepId,
        expected: {[field]: oldValue === undefined ? null : oldValue},
        change: {[field]: newValue},
      });
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      reportSaveFailed(saveKey, () => void saveSheepWeight(sheepId, field, value), saveAttempt);
      setNotice({kind: 'error', message: 'Weight save failed: ' + writeErrorMessage(err)});
      return;
    }
    // The `await loadAll()` that stood here is gone, and it is not merely
    // relocated: `patchBatch.run` awaits the DECLARED invalidation set, which
    // contains this page's resource AND the three others that read the row
    // that just changed. loadAll only ever refreshed this one.
  }

  async function saveMetaField(field: string, rawValue: string) {
    if (!canEdit || !batch) return;
    let value: string | number | null = rawValue;
    if (field === 'name') {
      value = (rawValue || '').trim();
      if (!value || value === batch.name) return;
    } else if (field === 'planned_process_date' || field === 'actual_process_date') {
      value = (rawValue || '').trim();
      if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
      if (value === (batch[field] || '')) return;
      if (value === '') value = null;
      // Clearing the actual date on a COMPLETE batch would drop a finished
      // batch out of production and carcass-yield reporting, both of which
      // select on `actual_process_date IS NOT NULL`. Migration 206's trigger
      // refuses this write; catching it here means the operator gets a sentence
      // instead of a constraint error, and the stale input is put back.
      if (field === 'actual_process_date' && value === null && batch.status === 'complete') {
        setMetaDraft((m) => ({...m, actual_process_date: batch.actual_process_date || ''}));
        setNotice({
          kind: 'error',
          message: 'A complete batch keeps its actual process date. Change the status first to clear it.',
        });
        return;
      }
    } else if (field === 'processing_cost') {
      const trimmed = (rawValue || '').toString().trim();
      value = trimmed === '' ? null : parseFloat(trimmed);
      if (value != null && Number.isNaN(value)) return;
      const current = batch.processing_cost != null ? batch.processing_cost : null;
      if (value === current) return;
    } else if (field === 'notes') {
      value = rawValue || null;
      if ((value || '') === (batch.notes || '')) return;
    }
    setNotice(null);
    // MIGRATION 241: the one field, against the value the page READ; the server
    // writes the "<label> <old> → <new>" Activity row, labelling each field the
    // way the editors here always did (Renamed, Cost, Planned date, Actual date,
    // Notes) -- the label map now lives in the migration, not in this file.
    const saveKey = `sheep-batch-meta:${batch.id}:${field}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      await patchBatch.run({
        requestId: newSheepRequestId(),
        operation: 'batch.update',
        batchId: batch.id,
        expected: {[field]: batch[field] === undefined ? null : batch[field]},
        change: {[field]: value},
      });
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      reportSaveFailed(saveKey, () => void saveMetaField(field, rawValue), saveAttempt);
      setNotice({kind: 'error', message: 'Save failed: ' + writeErrorMessage(err)});
      return;
    }
  }

  async function saveStatus(nextStatus: string) {
    if (!canEdit || !batch) return;
    if (nextStatus === batch.status) return;
    setNotice(null);
    // Complete is the step that means "the processor is done", and it is the
    // ONLY step that sets actual_process_date. A send deliberately does not
    // stamp it (see migration 206), so without this the column stays null
    // forever and the batch never reaches production or carcass-yield
    // reporting, both of which select on `actual_process_date IS NOT NULL`.
    //
    // Required rather than inferred: the ship date is not the processing date,
    // and today's date is a guess. An already-entered date is preserved, which
    // is why this only blocks when the field is genuinely empty.
    //
    // The date is read from the DRAFT, not from `batch`. The date input saves on
    // blur, so typing a date and going straight to the select leaves `batch`
    // still holding the old value: checking the cached row would reject a
    // completion the operator had in fact just dated. The draft is what they
    // see.
    const patch: Record<string, unknown> = {status: nextStatus};
    if (nextStatus === 'complete') {
      const draftDate = ((metaDraft && metaDraft.actual_process_date) || '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(draftDate)) {
        setMetaDraft((m) => ({...m, status: batch.status || 'planned'}));
        setNotice({
          kind: 'error',
          message: 'Set the Actual Process Date below before marking this batch Complete.',
        });
        return;
      }
      // Sent WITH the status, in one write, so completion and its date cannot
      // land separately or half-land. Migration 206's trigger is the backstop:
      // it refuses a batch becoming complete without a date, and refuses the
      // date being cleared while it is complete.
      patch.actual_process_date = draftDate;
    }
    const saveKey = `sheep-batch-status:${batch.id}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      // MIGRATION 241: batch.status against the status this page READ; the
      // server writes the status.changed Activity row.
      await patchBatch.run({
        requestId: newSheepRequestId(),
        operation: 'batch.status',
        batchId: batch.id,
        expected: {status: batch.status},
        change: patch,
      });
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      reportSaveFailed(saveKey, () => void saveStatus(nextStatus), saveAttempt);
      setMetaDraft((m) => ({...m, status: batch.status || 'planned'}));
      setNotice({kind: 'error', message: 'Status save failed: ' + writeErrorMessage(err)});
      return;
    }
  }

  // Hand the weigh-in flow this batch to land in. The send itself still happens
  // at the end of a weigh-in session, where the weights are; this only carries
  // the destination forward so the operator does not have to find it again in
  // the picker.
  function sendSheepToProcessor() {
    if (!batch) return;
    navigate('/sheep/weighins', {
      state: {sendToBatch: {id: batch.id, name: batch.name || batch.id}},
    });
  }

  async function handleDetach(
    s: {id?: string; tag?: string | null} | null | undefined,
    rowTag: string | null | undefined,
  ) {
    if (!canEdit || !batch || !s || !s.id) return;
    setNotice(null);
    // Atomic detach via SECDEF RPC (migration 081): reverts the flock, writes
    // the undo transfer audit row, clears the weigh-ins, AND logs the
    // field.updated Activity event in one transaction — so no client logEvent
    // here. Business-rule blocks come back as {ok:false, reason}.
    const r = await detachSheepFromProcessingBatch(sb, {
      sheepId: s.id,
      batchId: batch.id,
      teamMember: authState && authState.name ? authState.name : null,
    });
    if (!r.ok) {
      const tag = s.tag || rowTag || r.tag || '?';
      if (r.reason === 'no_prior_flock') {
        setNotice({
          kind: 'warning',
          message:
            'Cannot auto-detach #' +
            tag +
            ': no prior flock recorded for this sheep + batch. Manually move via the Flocks tab.',
        });
      } else {
        setNotice({
          kind: 'error',
          message: 'Detach failed for #' + tag + ': ' + r.reason + (r.error ? ' — ' + r.error : ''),
        });
      }
      return;
    }
    invalidateSheepWeighInsCache();
    await loadAll();
  }

  async function handleDeleteBatch() {
    if (!canEdit || !batch) return;
    if (typeof window === 'undefined' || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete(
      'Delete this processing batch? Linked sheep will be detached and reverted to their prior flocks where possible.',
      async () => {
        setNotice(null);
        const sheepIds = sheepDetail()
          .map((r) => r.sheep_id)
          .filter(Boolean);
        const reverted: RpcEnvelope[] = [];
        const blocked: BlockedDetach[] = [];
        for (const sid of sheepIds) {
          const r = await detachSheepFromProcessingBatch(sb, {
            sheepId: sid,
            batchId: batch.id,
            teamMember: authState && authState.name ? authState.name : null,
          });
          if (r.ok) reverted.push(r);
          else blocked.push({...r, sheepId: sid});
        }
        // Atomic straggler-clear + batch delete via SECDEF RPC (migration 100):
        // clears any leftover sheep.processing_batch_id links and deletes the
        // batch, logging record.deleted in one transaction. The per-sheep detach
        // loop above already reverted/audited each animal individually.
        const del = await deleteBatch.run({
          batchId: batch.id,
          teamMember: authState && authState.name ? authState.name : null,
        });
        // ORDERING NOTE, because this is safe today for a reason that is not
        // obvious and would not survive being moved.
        //
        // `deleteBatch.run` now also settles the declared Query invalidations
        // before returning, and this line clears a DIFFERENT cache — the
        // module-global sheep weigh-in cache in src/lib/sheepCache.js, which
        // Query knows nothing about. So for one instant a Query refetch could
        // repopulate from a stale sheepCache.
        //
        // It cannot happen here: invalidation only REFETCHES resources with a
        // live observer, and the only one mounted on this surface is
        // sheep.batch.detail, whose tables are sheep_processing_batches and
        // sheep — no weigh-ins. The three weigh-in-reading resources in the
        // declared set are merely marked stale and will re-read after this
        // line has run. Move this call, or mount a weigh-in resource on this
        // page, and that argument stops holding.
        invalidateSheepWeighInsCache();
        if (!del.ok) {
          setNotice({
            kind: 'error',
            message: 'Delete failed: ' + (del.reason === 'rpc_error' ? del.error : del.reason),
          });
          return;
        }
        if (blocked.length > 0) {
          const lines = blocked.map((b) => '#' + (b.tag || b.sheepId || '?') + ' (' + b.reason + ')').join('\n');
          const message =
            'Batch deleted. ' +
            reverted.length +
            ' sheep reverted. ' +
            blocked.length +
            ' could not be auto-reverted:\n\n' +
            lines +
            '\n\nManually move them via the Flocks tab if needed.';
          navigate('/sheep/batches', {state: {notice: {kind: 'warning', message}}});
          return;
        }
        navigate('/sheep/batches');
      },
    );
  }

  if (loading) {
    return <RecordPageLoading Header={Header} />;
  }

  if (loadError) {
    return (
      <RecordPageLoadError
        backLabel="Back to Processing Batches"
        onBack={() => navigate('/sheep/batches')}
        notice={loadError}
        onRetry={loadAll}
        maxWidth={900}
        data-sheep-batch-load-error="true"
      />
    );
  }

  if (!batch) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Processing Batches"
        onBack={() => navigate('/sheep/batches')}
        message="Batch not found."
      />
    );
  }

  const rows = sheepDetail();
  const {totalLive, totalHang, yieldPct} = computeRuminantBatchTotals(rows);
  const isComplete = batch.status === 'complete';
  const entityLabel = batch.name || batchId;
  const draftKey = (sid: string, field: string) => `${batch.id}|${sid}|${field}`;
  const draftVal = (sid: string, field: string, curr: unknown) => {
    const k = draftKey(sid, field);
    return draft[k] != null ? draft[k] : curr != null ? String(curr) : '';
  };

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody maxWidth={900} data-sheep-batch-record-loaded="true">
        <RecordBackLink label="Back to Processing Batches" onBack={() => navigate('/sheep/batches')} />

        <RecordSequenceNav seq={recordSeq} currentId={batchId} onNavigate={navigateSeq} />

        <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12}}>
          <RecordTitle fontSize={22} margin={0}>
            {batch.name}
          </RecordTitle>
          <Badge status={processingStatusLabel(batch.status)} />
          <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>
            {rows.length} {rows.length === 1 ? 'sheep' : 'sheep'}
          </span>
          {/* The planned date IS the booking (mig 206): black date, gray "Booked" (Q14-A). */}
          {batch.planned_process_date &&
            (isComplete ? (
              <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>booked {fmt(batch.planned_process_date)}</span>
            ) : (
              <span style={{fontSize: 12}} data-sheep-batch-booked>
                <span style={{fontWeight: 700, color: 'var(--text-primary)'}}>{fmt(batch.planned_process_date)}</span>{' '}
                <span style={{color: 'var(--ink-muted)'}}>Booked</span>
              </span>
            ))}
          {batch.actual_process_date && (
            <span style={{fontSize: 12, color: 'var(--ok-ink)'}}>processed {fmt(batch.actual_process_date)}</span>
          )}
          {yieldPct && (
            <span style={{fontSize: 12, fontWeight: 600, color: 'var(--text-primary)'}}>{yieldPct + '% yield'}</span>
          )}
          {/* Deep link into the Processing Calendar record for this batch —
              only meaningful once a planned or actual process date exists. */}
          {(batch.actual_process_date || batch.planned_process_date) && (
            <>
              <Button
                variant="secondary"
                type="button"
                data-processing-source-link="sheep"
                onClick={() => navigateToProcessingRoute(navigate, processingSourceRoute('sheep', batch.id))}
              >
                View in Processing →
              </Button>
            </>
          )}
        </div>

        {notice && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}

        {/* Metadata editor — migrated to shared record-page controls (CP2).
            Save behavior (saveMetaField onBlur, saveStatus immediate) and data
            attributes are unchanged. */}
        <div data-sheep-batch-meta="1" style={{...recordFormCard, marginBottom: 12}}>
          <div className={recordFieldRowClass}>
            <span style={recordFieldLabel}>Name</span>
            <input
              value={metaDraft ? metaDraft.name : ''}
              disabled={!canEdit}
              data-sheep-batch-name
              onChange={(e) => setMetaDraft((m) => ({...m, name: e.target.value}))}
              onBlur={(e) => saveMetaField('name', e.target.value)}
              style={recordControl}
            />
          </div>
          <div className={recordFieldRowClass}>
            <span style={recordFieldLabel}>Status</span>
            <select
              value={metaDraft ? metaDraft.status : 'planned'}
              disabled={!canEdit}
              data-sheep-batch-status
              onChange={(e) => {
                const next = e.target.value;
                setMetaDraft((m) => ({...m, status: next}));
                saveStatus(next);
              }}
              style={recordControl}
            >
              {/* 'active' is normally reached by sending sheep, not chosen —
                  but it must be listed, or opening an In Process batch would
                  show a select whose value is not among its options and the
                  first save would silently demote it to Planned. */}
              <option value="planned">Planned</option>
              <option value="active">In Process</option>
              <option value="complete">Complete</option>
            </select>
          </div>
          <div className={recordFieldRowClass}>
            <span style={recordFieldLabel}>Processing Cost ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={metaDraft ? metaDraft.processing_cost : ''}
              disabled={!canEdit}
              data-sheep-batch-cost
              onChange={(e) => setMetaDraft((m) => ({...m, processing_cost: e.target.value}))}
              onBlur={(e) => saveMetaField('processing_cost', e.target.value)}
              style={recordControl}
            />
          </div>
          <div className={recordFieldRowClass}>
            <span style={recordFieldLabel}>Processor Date</span>
            <input
              type="date"
              value={metaDraft ? metaDraft.planned_process_date : ''}
              disabled={!canEdit}
              data-sheep-batch-planned-date
              onChange={(e) => setMetaDraft((m) => ({...m, planned_process_date: e.target.value}))}
              onBlur={(e) => saveMetaField('planned_process_date', e.target.value)}
              style={recordControl}
            />
          </div>
          <div className={recordFieldRowClass}>
            <span style={recordFieldLabel}>Actual Process Date</span>
            <input
              type="date"
              value={metaDraft ? metaDraft.actual_process_date : ''}
              disabled={!canEdit}
              data-sheep-batch-actual-date
              onChange={(e) => setMetaDraft((m) => ({...m, actual_process_date: e.target.value}))}
              onBlur={(e) => saveMetaField('actual_process_date', e.target.value)}
              style={recordControl}
            />
          </div>
          <div className={recordFieldRowClass}>
            <span style={recordFieldLabel}>Notes</span>
            <textarea
              value={metaDraft ? metaDraft.notes : ''}
              disabled={!canEdit}
              data-sheep-batch-notes
              rows={3}
              onChange={(e) => setMetaDraft((m) => ({...m, notes: e.target.value}))}
              onBlur={(e) => saveMetaField('notes', e.target.value)}
              style={recordTextarea}
            />
          </div>
        </div>

        {/* Stats + sheep weights */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-card)',
            boxShadow: 'var(--shadow-card)',
            padding: '14px 18px',
            marginBottom: 12,
          }}
        >
          <RuminantBatchStatsGrid>
            <RuminantBatchStat
              label="Live wt total"
              value={totalLive > 0 ? Math.round(totalLive).toLocaleString() + ' lb' : '—'}
            />
            <RuminantBatchStat
              label="Hanging wt"
              value={totalHang > 0 ? Math.round(totalHang).toLocaleString() + ' lb' : '—'}
            />
            <RuminantBatchStat
              label="Yield"
              value={yieldPct ? yieldPct + '%' : '—'}
              color={yieldPct ? 'var(--text-primary)' : 'var(--ink-faint)'}
            />
            <RuminantBatchStat
              label="Cost"
              value={batch.processing_cost ? '$' + batch.processing_cost.toLocaleString() : '—'}
            />
          </RuminantBatchStatsGrid>

          {rows.length > 0 ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8}}>
              {rows.map((r) => {
                const s = sheep.find((x) => x.id === r.sheep_id);
                const lv = parseFloat(String(r.live_weight));
                const hw = parseFloat(String(r.hanging_weight));
                const y = lv > 0 && hw > 0 ? Math.round((hw / lv) * 1000) / 10 : null;
                const weightDisabled = !canEdit || isComplete;
                const weightStyle: React.CSSProperties = {
                  fontSize: 13,
                  padding: '6px 8px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-control)',
                  fontFamily: 'inherit',
                  width: '100%',
                  minWidth: 70,
                  boxSizing: 'border-box',
                  background: weightDisabled ? 'var(--surface-2)' : 'var(--surface)',
                  color: 'var(--ink)',
                  opacity: 1,
                  WebkitTextFillColor: 'var(--ink)',
                };
                return (
                  <div key={r.sheep_id} data-batch-sheep-row={r.sheep_id} style={ruminantBatchRowCardStyle}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4}}>
                      <span style={{fontWeight: 700, color: 'var(--ink)', minWidth: 50}}>
                        {'#' + (r.tag || s?.tag || '?')}
                      </span>
                      <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>{s?.breed || '—'}</span>
                      {y && (
                        <span style={{fontSize: 11, fontWeight: 600, color: 'var(--text-primary)'}}>
                          {y + '% yield'}
                        </span>
                      )}
                      <span style={{flex: 1}} />
                      {canEdit && !isComplete && (
                        <button
                          type="button"
                          onClick={() => handleDetach(s || {id: r.sheep_id, tag: r.tag}, r.tag)}
                          title="Detach sheep from batch (reverts flock)"
                          data-batch-sheep-detach={r.sheep_id}
                          style={ruminantBatchDetachButtonStyle}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                      <label style={{flex: '1 1 120px', minWidth: 0}}>
                        <div style={ruminantBatchWeightLabelStyle}>Live wt (lb)</div>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="—"
                          value={draftVal(r.sheep_id, 'live', r.live_weight)}
                          disabled={weightDisabled}
                          data-batch-sheep-live-weight={r.sheep_id}
                          onChange={(e) => setDraft((p) => ({...p, [draftKey(r.sheep_id, 'live')]: e.target.value}))}
                          onBlur={(e) => {
                            saveSheepWeight(r.sheep_id, 'live_weight', e.target.value);
                            setDraft((p) => {
                              const x = {...p};
                              delete x[draftKey(r.sheep_id, 'live')];
                              return x;
                            });
                          }}
                          style={weightStyle}
                        />
                      </label>
                      <label style={{flex: '1 1 120px', minWidth: 0}}>
                        <div style={ruminantBatchWeightLabelStyle}>Hanging wt (lb)</div>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="—"
                          value={draftVal(r.sheep_id, 'hanging', r.hanging_weight)}
                          disabled={weightDisabled}
                          data-batch-sheep-hanging-weight={r.sheep_id}
                          onChange={(e) => setDraft((p) => ({...p, [draftKey(r.sheep_id, 'hanging')]: e.target.value}))}
                          onBlur={(e) => {
                            saveSheepWeight(r.sheep_id, 'hanging_weight', e.target.value);
                            setDraft((p) => {
                              const x = {...p};
                              delete x[draftKey(r.sheep_id, 'hanging')];
                              return x;
                            });
                          }}
                          style={weightStyle}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div>
              <div style={{fontSize: 12, color: 'var(--ink-faint)', fontStyle: 'italic'}}>
                No sheep attached yet. Sheep enter this batch through the send confirmation at the end of a sheep
                weigh-in session — flagging a sheep at the chute marks it, but does not attach it.
              </div>
              {canEdit && !isComplete && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={sendSheepToProcessor}
                  data-sheep-batch-send-to-processor={batch.id}
                  style={{marginTop: 10}}
                >
                  Send sheep to processor
                </Button>
              )}
            </div>
          )}

          {rows.length > 0 && !isComplete && (
            <div style={{display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap'}}>
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontStyle: 'italic', flex: 1, minWidth: 220}}>
                Sheep enter this batch through the send confirmation at the end of a sheep weigh-in session.
              </span>
              {canEdit && (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={sendSheepToProcessor}
                  data-sheep-batch-send-to-processor={batch.id}
                >
                  Send more sheep
                </Button>
              )}
            </div>
          )}

          {canEdit && (
            <div style={{display: 'flex', gap: 8, marginTop: 10}}>
              <span style={{flex: 1}} />
              <Button variant="danger" type="button" onClick={handleDeleteBatch} data-sheep-batch-delete={batch.id}>
                Delete batch
              </Button>
            </div>
          )}
        </div>

        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="sheep.processing"
          entityId={batch.id}
          entityLabel={entityLabel}
        />
      </RecordPageBody>
    </RecordPageFrame>
  );
}
